import type Phaser from "phaser";

/**
 * Page screenshots for the feedback button. A WebGL canvas reads back as empty once the frame has been
 * presented, so the capture goes through the renderer's own snapshot, which reads the pixels inside the
 * render pass and hands back a loaded image; a canvas renderer has no such problem and is drawn directly.
 * The result is scaled down to at most `MAX_SCREENSHOT_WIDTH` so a report stays small enough to post.
 */

/**
 * Widest screenshot a report carries, in pixels.
 */
export const MAX_SCREENSHOT_WIDTH = 1280;

const SNAPSHOT_TIMEOUT_MS = 3000;

/**
 * The renderer method Phaser's WebGL renderer exposes for reading the presented frame back.
 */
interface SnapshotCapable {
  readonly snapshot: (callback: (result: unknown) => void, type?: string) => unknown;
}

/**
 * Reports whether a renderer can snapshot itself.
 *
 * @param renderer The game's renderer.
 * @returns True when the renderer exposes a snapshot method.
 */
function canSnapshot(renderer: unknown): renderer is SnapshotCapable {
  return typeof renderer === "object" && renderer !== null && typeof (renderer as Partial<SnapshotCapable>).snapshot === "function";
}

/**
 * Reads the presented frame back as an image, or falls back to the canvas element itself.
 *
 * @param game The running game.
 * @returns The image or canvas to draw from, or null when neither is available before the timeout.
 */
async function snapshotSource(game: Phaser.Game): Promise<HTMLImageElement | HTMLCanvasElement | null> {
  const renderer: unknown = game.renderer;
  if (!canSnapshot(renderer)) {
    return game.canvas instanceof HTMLCanvasElement ? game.canvas : null;
  }
  return new Promise<HTMLImageElement | HTMLCanvasElement | null>((resolve) => {
    const timer = setTimeout(() => resolve(null), SNAPSHOT_TIMEOUT_MS);
    renderer.snapshot((result: unknown) => {
      clearTimeout(timer);
      resolve(result instanceof HTMLImageElement ? result : null);
    });
  });
}

/**
 * Draws a source into a canvas no wider than the limit and reads it back as a PNG data URL.
 *
 * @param source The image or canvas to draw.
 * @param maxWidth Widest result in pixels.
 * @returns The data URL, or null when the source is empty or a 2D context is refused.
 */
function downscale(source: HTMLImageElement | HTMLCanvasElement, maxWidth: number): string | null {
  const width = source instanceof HTMLImageElement ? source.naturalWidth : source.width;
  const height = source instanceof HTMLImageElement ? source.naturalHeight : source.height;
  if (width === 0 || height === 0) {
    return null;
  }
  const scale = Math.min(1, maxWidth / width);
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(width * scale));
  canvas.height = Math.max(1, Math.round(height * scale));
  const context = canvas.getContext("2d");
  if (context === null) {
    return null;
  }
  context.drawImage(source, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL("image/png");
}

/**
 * Captures the game's canvas as a PNG data URL for a feedback report.
 *
 * @param game The running game, or null before one exists.
 * @param maxWidth Widest result in pixels.
 * @returns The data URL, or null when nothing could be captured.
 */
export async function captureGameScreenshot(game: Phaser.Game | null, maxWidth: number = MAX_SCREENSHOT_WIDTH): Promise<string | null> {
  if (game === null) {
    return null;
  }
  const source = await snapshotSource(game);
  return source === null ? null : downscale(source, maxWidth);
}
